import { useCallback, useMemo, useState } from "react";
import { Buffer } from "buffer";
import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import { formatUnits, parseUnits } from "ethers";
import { useNetworksStore } from "store/networksStore";
import { isSolanaNetwork } from "types/networks";
import { Fee } from "types/fee";
import { useSolanaWallet } from "./useSolanaWallet";

const FACTORY_SEED = new TextEncoder().encode("factory_config");
const SOLANA_RPC_URL =
  "https://mainnet.helius-rpc.com/?api-key=81e1063c-aa07-4ba5-ac0f-4b311401937e";
const FACTORY_CONFIG_FIXED_DATA_OFFSET = 160;
const FEE_TIER_SIZE = 26;
const MAX_U64 = BigInt("18446744073709551615");
const ONE = BigInt(1);
const ZERO = BigInt(0);
const MAX_PERCENTAGE_BPS = BigInt(10000);

const SET_DEPOSIT_FEE_TIERS_DISCRIMINATOR = Uint8Array.from([
  47, 140, 232, 77, 4, 130, 67, 1,
]);
const SET_WITHDRAW_FEE_TIERS_DISCRIMINATOR = Uint8Array.from([
  76, 41, 109, 44, 214, 49, 123, 55,
]);
const SET_POOL_CREATION_FEE_DISCRIMINATOR = Uint8Array.from([
  134, 204, 55, 5, 234, 4, 24, 108,
]);
const SET_TREASURY_DISCRIMINATOR = Uint8Array.from([
  57, 97, 196, 95, 195, 206, 106, 136,
]);
const SET_JUPITER_PROGRAM_DISCRIMINATOR = Uint8Array.from([
  160, 114, 187, 234, 183, 231, 123, 230,
]);
const MIGRATE_FACTORY_DISCRIMINATOR = Uint8Array.from([
  248, 245, 238, 73, 119, 140, 136, 255,
]);
const SET_SWAP_EXECUTOR_DISCRIMINATOR = Uint8Array.from([
  82, 161, 157, 223, 157, 118, 160, 207,
]);
const SWEEP_FACTORY_DISCRIMINATOR = Uint8Array.from([
  247, 137, 209, 11, 103, 49, 110, 128,
]);
const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
const TOKEN_PROGRAM_ID = new PublicKey(
  "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);
const TOKEN_2022_PROGRAM_ID = new PublicKey(
  "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
);

type SolanaFeeTier = {
  from: bigint;
  to: bigint;
  percentage: number;
  fixedFee: bigint;
};

const trimZeroes = (value: string): string => {
  if (!value.includes(".")) return value;
  const trimmed = value.replace(/\.?0+$/, "");
  return trimmed.length ? trimmed : "0";
};

const readU16 = (bytes: Uint8Array, offset: number): number =>
  new DataView(bytes.buffer, bytes.byteOffset + offset, 2).getUint16(0, true);

const readU32 = (bytes: Uint8Array, offset: number): number =>
  new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0, true);

const readU64 = (bytes: Uint8Array, offset: number): bigint =>
  new DataView(bytes.buffer, bytes.byteOffset + offset, 8).getBigUint64(
    0,
    true
  );

const writeU16 = (view: DataView, offset: number, value: number) => {
  view.setUint16(offset, value, true);
};

const writeU32 = (view: DataView, offset: number, value: number) => {
  view.setUint32(offset, value, true);
};

const writeU64 = (view: DataView, offset: number, value: bigint) => {
  view.setBigUint64(offset, value, true);
};

const toUiAmount = (amount: bigint, decimals: number): string =>
  trimZeroes(formatUnits(amount, decimals));

const decodeFeeTiers = (
  bytes: Uint8Array,
  initialOffset: number,
  currencyDecimals: number
) => {
  let offset = initialOffset;
  const feeTiersCount = readU32(bytes, offset);
  offset += 4;

  const decoded: Fee[] = [];

  for (let i = 0; i < feeTiersCount; i += 1) {
    if (offset + FEE_TIER_SIZE > bytes.length) {
      throw new Error("Invalid factory config account layout.");
    }

    const from = readU64(bytes, offset);
    const percentage = readU16(bytes, offset + 16);
    const fixedFee = readU64(bytes, offset + 18);

    decoded.push({
      from: toUiAmount(from, currencyDecimals),
      percentage: toUiAmount(BigInt(percentage), 2),
      fixedFee: toUiAmount(fixedFee, currencyDecimals),
    });

    offset += FEE_TIER_SIZE;
  }

  return {
    feeTiers: decoded,
    offset,
  };
};

const encodeFeeTiers = (feeTiers: SolanaFeeTier[]): Uint8Array => {
  const encoded = new Uint8Array(4 + feeTiers.length * FEE_TIER_SIZE);
  const view = new DataView(encoded.buffer);

  writeU32(view, 0, feeTiers.length);

  let offset = 4;
  feeTiers.forEach((feeTier) => {
    writeU64(view, offset, feeTier.from);
    writeU64(view, offset + 8, feeTier.to);
    writeU16(view, offset + 16, feeTier.percentage);
    writeU64(view, offset + 18, feeTier.fixedFee);
    offset += FEE_TIER_SIZE;
  });

  return encoded;
};

const deriveAssociatedTokenAddress = (
  mint: PublicKey,
  owner: PublicKey,
  tokenProgram: PublicKey
): PublicKey =>
  PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  )[0];

export const useSolanaFactoryContract = () => {
  const { currentNetwork } = useNetworksStore();
  const { address: walletAddress, sendTransaction } = useSolanaWallet();
  const [poolCreationFeeValue, setPoolCreationFeeValue] = useState("");
  const [factoryOwner, setFactoryOwner] = useState("");
  const [operationCount, setOperationCount] = useState("");
  const [treasuryAddress, setTreasuryAddress] = useState("");
  const [jupiterProgramAddress, setJupiterProgramAddress] = useState("");
  const [swapExecutorAddress, setSwapExecutorAddress] = useState("");
  const [isLoadingState, setIsLoadingState] = useState(false);
  const [activeRpcIndex, setActiveRpcIndex] = useState(0);

  const currencyDecimals = currentNetwork?.depositTokens?.[0]?.decimals ?? 6;
  const isCurrentSolanaNetwork = isSolanaNetwork(currentNetwork);
  const rpcUrls = useMemo(
    () => (isCurrentSolanaNetwork ? [SOLANA_RPC_URL] : []),
    [isCurrentSolanaNetwork]
  );

  const connections = useMemo(
    () => rpcUrls.map((url) => new Connection(url, "confirmed")),
    [rpcUrls]
  );

  const programId = useMemo(() => {
    if (!isCurrentSolanaNetwork || !currentNetwork?.programId) return null;

    try {
      return new PublicKey(currentNetwork.programId);
    } catch (error) {
      console.error("Invalid Solana program id in network config", error);
      return null;
    }
  }, [isCurrentSolanaNetwork, currentNetwork?.programId]);

  const deriveFactoryConfigPda = useCallback(() => {
    if (!programId) return null;
    const [factoryConfigPda] = PublicKey.findProgramAddressSync(
      [FACTORY_SEED],
      programId
    );
    return factoryConfigPda;
  }, [programId]);

  const factoryConfigAddress = useMemo(
    () => deriveFactoryConfigPda()?.toBase58() ?? "",
    [deriveFactoryConfigPda]
  );

  const parseFeeTiersFromForm = useCallback(
    (feeTiers: Fee[]): SolanaFeeTier[] => {
      return feeTiers.map((feeTier, index) => {
        const from =
          index === 0 ? ONE : parseUnits(feeTier.from || "0", currencyDecimals);

        const to =
          index === feeTiers.length - 1
            ? MAX_U64
            : parseUnits(feeTiers[index + 1]?.from || "0", currencyDecimals) -
              ONE;

        const percentage = parseUnits(feeTier.percentage || "0", 2);
        const fixedFee = parseUnits(feeTier.fixedFee || "0", currencyDecimals);

        if (to < from) {
          throw new Error("Invalid fee ranges: next `from` must be greater.");
        }

        const hasPercentage = percentage > ZERO;
        const hasFixedFee = fixedFee > ZERO;

        if (hasPercentage === hasFixedFee) {
          throw new Error(
            "Each fee tier must have either percentage or fixed fee."
          );
        }

        if (percentage > MAX_PERCENTAGE_BPS) {
          throw new Error("Fee percentage cannot exceed 100%.");
        }

        return {
          from,
          to,
          percentage: Number(percentage),
          fixedFee,
        };
      });
    },
    [currencyDecimals]
  );

  const sendInstructionWithAccounts = useCallback(
    async ({
      instructionData,
      keys,
    }: {
      instructionData: Uint8Array;
      keys: {
        pubkey: PublicKey;
        isSigner: boolean;
        isWritable: boolean;
      }[];
    }) => {
      if (!connections.length || !programId) {
        throw new Error("Solana network RPC or program is not configured.");
      }
      if (!walletAddress) {
        throw new Error("Connect a Solana wallet first.");
      }

      const authority = new PublicKey(walletAddress);

      let lastError: unknown = null;

      for (let i = 0; i < connections.length; i += 1) {
        const candidateConnection = connections[i];
        try {
          const instruction = new TransactionInstruction({
            programId,
            keys,
            data: Buffer.from(instructionData),
          });

          const transaction = new Transaction().add(instruction);
          const latestBlockhash =
            await candidateConnection.getLatestBlockhash();

          transaction.recentBlockhash = latestBlockhash.blockhash;
          transaction.feePayer = authority;

          const signature = await sendTransaction({
            transaction,
            connection: candidateConnection,
            latestBlockhash,
          });

          if (i !== activeRpcIndex) {
            setActiveRpcIndex(i);
          }

          return signature;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error("Failed to submit Solana transaction.");
    },
    [
      connections,
      programId,
      walletAddress,
      sendTransaction,
      activeRpcIndex,
    ]
  );

  const sendFactoryInstruction = useCallback(
    async (instructionData: Uint8Array) => {
      const authority = walletAddress ? new PublicKey(walletAddress) : null;
      const factoryConfigPda = deriveFactoryConfigPda();

      if (!authority || !factoryConfigPda) {
        throw new Error("Factory config PDA or wallet address is unavailable.");
      }

      return sendInstructionWithAccounts({
        instructionData,
        keys: [
          { pubkey: authority, isSigner: true, isWritable: false },
          { pubkey: factoryConfigPda, isSigner: false, isWritable: true },
        ],
      });
    },
    [walletAddress, deriveFactoryConfigPda, sendInstructionWithAccounts]
  );

  const getFactoryState = useCallback(async () => {
    if (!connections.length || !programId) {
      return null;
    }

    const factoryConfigPda = deriveFactoryConfigPda();
    if (!factoryConfigPda) {
      return null;
    }

    setIsLoadingState(true);

    try {
      let accountInfo: Awaited<
        ReturnType<Connection["getAccountInfo"]>
      > | null = null;
      let successfulRpcIndex = -1;

      for (let i = 0; i < connections.length; i += 1) {
        try {
          const candidateAccountInfo = await connections[i].getAccountInfo(
            factoryConfigPda
          );

          if (!candidateAccountInfo?.data) {
            continue;
          }

          accountInfo = candidateAccountInfo;
          successfulRpcIndex = i;
          break;
        } catch (_error) {
          continue;
        }
      }

      if (!accountInfo?.data) {
        setFactoryOwner("");
        setOperationCount("");
        setPoolCreationFeeValue("");
        setTreasuryAddress("");
        setJupiterProgramAddress("");
        setSwapExecutorAddress("");
        return null;
      }

      if (successfulRpcIndex >= 0 && successfulRpcIndex !== activeRpcIndex) {
        setActiveRpcIndex(successfulRpcIndex);
      }

      const bytes = new Uint8Array(accountInfo.data);

      if (bytes.length < 120) {
        throw new Error("Invalid factory config account size.");
      }

      const authority = new PublicKey(bytes.slice(8, 40)).toBase58();
      const treasury = new PublicKey(bytes.slice(40, 72)).toBase58();
      const isLegacyLayout = bytes.length < FACTORY_CONFIG_FIXED_DATA_OFFSET;
      const operationCountValue = isLegacyLayout ? ZERO : readU64(bytes, 80);
      const poolCreationFee = isLegacyLayout ? readU64(bytes, 80) : readU64(bytes, 88);
      const jupiterProgram = new PublicKey(
        isLegacyLayout ? bytes.slice(88, 120) : bytes.slice(96, 128)
      ).toBase58();
      const swapExecutor = isLegacyLayout
        ? PublicKey.default.toBase58()
        : new PublicKey(bytes.slice(128, 160)).toBase58();

      setFactoryOwner(authority);
      setOperationCount(operationCountValue.toString());
      setTreasuryAddress(treasury);
      setJupiterProgramAddress(jupiterProgram);
      setSwapExecutorAddress(swapExecutor);
      setPoolCreationFeeValue(toUiAmount(poolCreationFee, currencyDecimals));

      let offset = isLegacyLayout ? 120 : FACTORY_CONFIG_FIXED_DATA_OFFSET;
      const { feeTiers: depositFeeTiers, offset: depositOffset } =
        decodeFeeTiers(bytes, offset, currencyDecimals);

      offset = depositOffset;

      const { feeTiers: withdrawFeeTiers } = decodeFeeTiers(
        bytes,
        offset,
        currencyDecimals
      );

      return [depositFeeTiers, withdrawFeeTiers];
    } catch (error) {
      console.error("Error fetching Solana factory settings", error);
      return null;
    } finally {
      setIsLoadingState(false);
    }
  }, [
    connections,
    currencyDecimals,
    deriveFactoryConfigPda,
    programId,
    activeRpcIndex,
  ]);

  const setPoolCreationFee = useCallback(
    async (value: bigint) => {
      if (value < ZERO || value > MAX_U64) {
        throw new Error("Pool creation fee is out of range for Solana.");
      }

      const instructionData = new Uint8Array(
        SET_POOL_CREATION_FEE_DISCRIMINATOR.length + 8
      );
      instructionData.set(SET_POOL_CREATION_FEE_DISCRIMINATOR, 0);

      const view = new DataView(instructionData.buffer);
      writeU64(view, SET_POOL_CREATION_FEE_DISCRIMINATOR.length, value);

      await sendFactoryInstruction(instructionData);
    },
    [sendFactoryInstruction]
  );

  const setFeesInfo = useCallback(
    async ({ fees, isDeposit }: { fees: Fee[]; isDeposit: boolean }) => {
      const parsedFeeTiers = parseFeeTiersFromForm(fees);
      const encodedFeeTiers = encodeFeeTiers(parsedFeeTiers);

      const discriminator = isDeposit
        ? SET_DEPOSIT_FEE_TIERS_DISCRIMINATOR
        : SET_WITHDRAW_FEE_TIERS_DISCRIMINATOR;

      const instructionData = new Uint8Array(
        discriminator.length + encodedFeeTiers.length
      );

      instructionData.set(discriminator, 0);
      instructionData.set(encodedFeeTiers, discriminator.length);

      await sendFactoryInstruction(instructionData);
    },
    [parseFeeTiersFromForm, sendFactoryInstruction]
  );

  const setTreasury = useCallback(
    async (newTreasury: string) => {
      const treasuryPubkey = new PublicKey(newTreasury);

      const instructionData = new Uint8Array(
        SET_TREASURY_DISCRIMINATOR.length + 32
      );
      instructionData.set(SET_TREASURY_DISCRIMINATOR, 0);
      instructionData.set(
        treasuryPubkey.toBytes(),
        SET_TREASURY_DISCRIMINATOR.length
      );

      await sendFactoryInstruction(instructionData);
    },
    [sendFactoryInstruction]
  );

  const setJupiterProgram = useCallback(
    async (newJupiterProgram: string) => {
      const jupiterProgramPubkey = new PublicKey(newJupiterProgram);

      if (jupiterProgramPubkey.equals(PublicKey.default)) {
        throw new Error("Jupiter program cannot be the default address.");
      }

      const instructionData = new Uint8Array(
        SET_JUPITER_PROGRAM_DISCRIMINATOR.length + 32
      );
      instructionData.set(SET_JUPITER_PROGRAM_DISCRIMINATOR, 0);
      instructionData.set(
        jupiterProgramPubkey.toBytes(),
        SET_JUPITER_PROGRAM_DISCRIMINATOR.length
      );

      await sendFactoryInstruction(instructionData);
    },
    [sendFactoryInstruction]
  );

  const setSwapExecutor = useCallback(
    async (newSwapExecutor: string) => {
      const swapExecutorPubkey = new PublicKey(newSwapExecutor);

      if (swapExecutorPubkey.equals(PublicKey.default)) {
        throw new Error("Swap executor cannot be the default address.");
      }

      const instructionData = new Uint8Array(
        SET_SWAP_EXECUTOR_DISCRIMINATOR.length + 32
      );
      instructionData.set(SET_SWAP_EXECUTOR_DISCRIMINATOR, 0);
      instructionData.set(
        swapExecutorPubkey.toBytes(),
        SET_SWAP_EXECUTOR_DISCRIMINATOR.length
      );

      await sendFactoryInstruction(instructionData);
    },
    [sendFactoryInstruction]
  );

  const migrateFactory = useCallback(
    async ({
      operationCount,
      swapExecutor,
    }: {
      operationCount: bigint;
      swapExecutor: string;
    }) => {
      if (!connections.length || !programId) {
        throw new Error("Solana network RPC or program is not configured.");
      }
      if (!walletAddress) {
        throw new Error("Connect a Solana wallet first.");
      }

      const authority = new PublicKey(walletAddress);
      const factoryConfigPda = deriveFactoryConfigPda();
      if (!factoryConfigPda) {
        throw new Error("Factory config PDA is unavailable.");
      }

      const swapExecutorPubkey = new PublicKey(swapExecutor);
      const instructionData = new Uint8Array(
        MIGRATE_FACTORY_DISCRIMINATOR.length + 8 + 32
      );
      instructionData.set(MIGRATE_FACTORY_DISCRIMINATOR, 0);
      const view = new DataView(instructionData.buffer);
      writeU64(view, MIGRATE_FACTORY_DISCRIMINATOR.length, operationCount);
      instructionData.set(
        swapExecutorPubkey.toBytes(),
        MIGRATE_FACTORY_DISCRIMINATOR.length + 8
      );

      await sendInstructionWithAccounts({
        instructionData,
        keys: [
          { pubkey: authority, isSigner: true, isWritable: true },
          { pubkey: factoryConfigPda, isSigner: false, isWritable: true },
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
        ],
      });
    },
    [
      connections,
      programId,
      walletAddress,
      deriveFactoryConfigPda,
      sendInstructionWithAccounts,
    ]
  );

  const sweepFactoryToken = useCallback(
    async ({ mintAddress, amount }: { mintAddress: string; amount: string }) => {
      if (!connections.length || !walletAddress || !programId) {
        throw new Error("Solana network RPC, program, or wallet is unavailable.");
      }
      if (!treasuryAddress) {
        throw new Error("Treasury address is not configured.");
      }

      const authority = new PublicKey(walletAddress);
      const factoryConfigPda = deriveFactoryConfigPda();
      if (!factoryConfigPda) {
        throw new Error("Factory config PDA is unavailable.");
      }

      const mint = new PublicKey(mintAddress);
      const treasury = new PublicKey(treasuryAddress);
      const candidateConnection = connections[activeRpcIndex] ?? connections[0];

      if (!candidateConnection) {
        throw new Error("No active Solana RPC connection.");
      }

      const mintAccountInfo = await candidateConnection.getAccountInfo(mint);
      if (!mintAccountInfo?.data) {
        throw new Error("Mint account was not found.");
      }

      const tokenProgram = mintAccountInfo.owner;
      const isSupportedTokenProgram =
        tokenProgram.equals(TOKEN_PROGRAM_ID) ||
        tokenProgram.equals(TOKEN_2022_PROGRAM_ID);

      if (!isSupportedTokenProgram) {
        throw new Error("Unsupported token program for sweep.");
      }

      const mintDecimals = mintAccountInfo.data[44];
      const rawAmount = parseUnits(amount || "0", mintDecimals);

      if (rawAmount <= ZERO || rawAmount > MAX_U64) {
        throw new Error("Sweep amount must be greater than zero.");
      }

      const factoryTokenAccount = deriveAssociatedTokenAddress(
        mint,
        factoryConfigPda,
        tokenProgram
      );
      const treasuryTokenAccount = deriveAssociatedTokenAddress(
        mint,
        treasury,
        tokenProgram
      );

      const [factoryTokenAccountInfo, treasuryTokenAccountInfo] =
        await Promise.all([
          candidateConnection.getAccountInfo(factoryTokenAccount),
          candidateConnection.getAccountInfo(treasuryTokenAccount),
        ]);

      if (!factoryTokenAccountInfo?.data) {
        throw new Error("Factory token account for this mint does not exist.");
      }
      if (!treasuryTokenAccountInfo?.data) {
        throw new Error("Treasury token account for this mint does not exist.");
      }

      const instructionData = new Uint8Array(
        SWEEP_FACTORY_DISCRIMINATOR.length + 8
      );
      instructionData.set(SWEEP_FACTORY_DISCRIMINATOR, 0);

      const view = new DataView(instructionData.buffer);
      writeU64(view, SWEEP_FACTORY_DISCRIMINATOR.length, rawAmount);

      await sendInstructionWithAccounts({
        instructionData,
        keys: [
          { pubkey: authority, isSigner: true, isWritable: false },
          { pubkey: factoryConfigPda, isSigner: false, isWritable: false },
          { pubkey: mint, isSigner: false, isWritable: false },
          { pubkey: factoryTokenAccount, isSigner: false, isWritable: true },
          { pubkey: treasuryTokenAccount, isSigner: false, isWritable: true },
          { pubkey: tokenProgram, isSigner: false, isWritable: false },
        ],
      });
    },
    [
      connections,
      walletAddress,
      programId,
      treasuryAddress,
      deriveFactoryConfigPda,
      activeRpcIndex,
      sendInstructionWithAccounts,
    ]
  );

  return {
    getFactoryState,
    poolCreationFeeValue,
    factoryOwner,
    operationCount,
    treasuryAddress,
    jupiterProgramAddress,
    swapExecutorAddress,
    isLoadingState,
    setPoolCreationFee,
    setFeesInfo,
    setTreasury,
    setJupiterProgram,
    setSwapExecutor,
    migrateFactory,
    sweepFactoryToken,
    factoryConfigAddress,
  };
};
